import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CountryFlag from "./CountryFlag";
import { ChevronDown } from "lucide-react";

const LanguageSelector = ({ onLanguageChange }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: "de", name: "Deutsch", flag: "DE" },
    { code: "en", name: "English", flag: "GB" },
    { code: "zh", name: "中文", flag: "CN" },
    { code: "hi", name: "हिन्दी", flag: "IN" },
    { code: "es", name: "Español", flag: "ES" },
    { code: "fr", name: "Français", flag: "FR" },
    { code: "ru", name: "Русский", flag: "RU" },
    { code: "ar", name: "العربية", flag: "SA" },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) ||
    languages.find((lang) => i18n.language?.startsWith(lang.code)) ||
    languages.find((lang) => lang.code === i18n.resolvedLanguage) ||
    languages[0];

  const handleLanguageChange = (languageCode) => {
    // Erst nach changeLanguage steht die neue Sprache im localStorage. Der
    // Impressum-Badge in index.html lebt ausserhalb von React, liest genau von
    // dort und braucht dieses Signal, sonst bleibt er beim Stand vom Seitenaufruf.
    Promise.resolve(i18n.changeLanguage(languageCode)).then(() => {
      window.dispatchEvent(new Event("dk-lang-changed"));
    });
    setIsOpen(false);
    // Call the callback if provided
    if (onLanguageChange) {
      onLanguageChange();
    }
  };

  const handleKeyDown = (event, languageCode) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleLanguageChange(languageCode);
    }
  };

  const handleToggleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === "Escape" && isOpen) {
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="relative"
      ref={dropdownRef}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-label="Select language"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleToggleKeyDown}
        className="flex items-center space-x-2 text-white hover:text-[#C5A66A] transition-all duration-300 font-medium cursor-pointer hover:scale-105"
        aria-label={`Current language: ${currentLanguage.name}. Click to change language`}
      >
        <CountryFlag
          countryCode={currentLanguage.flag}
          style={{
            width: "auto",
            height: "20px",
          }}
          alt={`${currentLanguage.name} flag`}
          title={`${currentLanguage.name} flag`}
        />

        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <ul
          className="absolute lg:right-0 left-0 mt-2 w-full lg:w-48 bg-[#1a2b42] border border-gray-600 rounded-lg shadow-lg z-50"
          role="listbox"
          aria-label="Available languages"
        >
          {languages.map((language) => (
            <li
              key={language.code}
              role="option"
              aria-selected={i18n.language === language.code}
            >
              <button
                onClick={() => handleLanguageChange(language.code)}
                onKeyDown={(e) => handleKeyDown(e, language.code)}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-[#0F1B2D] transition-colors duration-200 ${
                  i18n.language === language.code
                    ? "bg-[#C5A66A] text-[#0F1B2D]"
                    : "text-white"
                }`}
                aria-label={`Select ${language.name} language`}
              >
                <CountryFlag
                  countryCode={language.flag}
                  style={{
                    width: "20px",
                    height: "15px",
                  }}
                  alt={`${language.name} flag`}
                  title={`${language.name} flag`}
                />
                <span>{language.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
